package com.matchpredictor.web

import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlin.js.Date
import kotlin.math.ceil

@Serializable
data class PendingPrediction(
    val userId: Long,
    val pollId: Long,
    val choice: MatchOutcome,
    val submitAtMillis: Double
)

internal class PendingPredictionQueue(initial: List<PendingPrediction> = emptyList()) {
    private val entries = linkedMapOf<String, PendingPrediction>()

    init {
        restore(initial)
    }

    fun values(): List<PendingPrediction> = entries.values.toList()

    fun get(userId: Long, pollId: Long): PendingPrediction? = entries[key(userId, pollId)]

    fun select(
        userId: Long,
        pollId: Long,
        choice: MatchOutcome,
        nowMillis: Double,
        confirmationMillis: Double
    ): PendingPrediction {
        val key = key(userId, pollId)
        val existing = entries[key]
        return PendingPrediction(
            userId = userId,
            pollId = pollId,
            choice = choice,
            submitAtMillis = existing?.submitAtMillis ?: (nowMillis + confirmationMillis)
        ).also { entries[key] = it }
    }

    fun remove(userId: Long, pollId: Long): PendingPrediction? = entries.remove(key(userId, pollId))

    fun clearUser(userId: Long): List<PendingPrediction> {
        val removed = entries.values.filter { it.userId == userId }
        removed.forEach { entries.remove(key(it.userId, it.pollId)) }
        return removed
    }

    fun restore(items: List<PendingPrediction>) {
        items.forEach { entries[key(it.userId, it.pollId)] = it }
    }

    private fun key(userId: Long, pollId: Long): String = "$userId:$pollId"
}

/**
 * Owns delayed prediction submission independently from poll-card DOM.
 *
 * Pending choices are persisted so SPA remounts and browser reloads cannot
 * silently cancel the ten-second confirmation window.
 */
object PendingPredictions {
    private const val STORAGE_KEY = "mp_pending_predictions_v1"
    private const val CONFIRMATION_MS = 10_000.0

    private val json = Json { ignoreUnknownKeys = true }
    private val scope = MainScope()
    private val queue = PendingPredictionQueue()
    private val timeoutIds = mutableMapOf<String, Int>()
    private val submittingKeys = mutableSetOf<String>()
    private var loaded = false
    private var activeUserId: Long? = null
    private var submitter: suspend (PendingPrediction) -> Unit = {}
    private var onSubmitted: (PendingPrediction) -> Unit = {}
    private var onFailure: (PendingPrediction, Throwable) -> Unit = { _, _ -> }

    fun activate(
        userId: Long,
        submit: suspend (PendingPrediction) -> Unit,
        submitted: (PendingPrediction) -> Unit,
        failed: (PendingPrediction, Throwable) -> Unit
    ) {
        loadOnce()
        timeoutIds.values.forEach { window.clearTimeout(it) }
        timeoutIds.clear()
        submittingKeys.clear()
        activeUserId = userId
        submitter = submit
        onSubmitted = submitted
        onFailure = failed
        queue.values().filter { it.userId == userId }.forEach(::schedule)
    }

    fun get(userId: Long, pollId: Long): PendingPrediction? {
        loadOnce()
        return queue.get(userId, pollId)
    }

    fun isSubmitting(userId: Long, pollId: Long): Boolean =
        key(userId, pollId) in submittingKeys

    fun select(userId: Long, pollId: Long, choice: MatchOutcome): PendingPrediction {
        loadOnce()
        val key = key(userId, pollId)
        val existing = queue.get(userId, pollId)
        if (key in submittingKeys) return existing ?: error("Prediction submission is already in progress")
        val pending = queue.select(
            userId = userId,
            pollId = pollId,
            choice = choice,
            nowMillis = Date.now(),
            confirmationMillis = CONFIRMATION_MS
        )
        persist()
        if (existing == null) schedule(pending)
        return pending
    }

    fun cancel(userId: Long, pollId: Long) {
        val key = key(userId, pollId)
        if (key in submittingKeys) return
        queue.remove(userId, pollId)
        timeoutIds.remove(key)?.let { window.clearTimeout(it) }
        persist()
    }

    fun clearForUser(userId: Long) {
        queue.clearUser(userId).map { key(it.userId, it.pollId) }.forEach { key ->
            timeoutIds.remove(key)?.let { window.clearTimeout(it) }
            submittingKeys.remove(key)
        }
        if (activeUserId == userId) activeUserId = null
        persist()
    }

    fun remainingSeconds(pending: PendingPrediction): Int =
        ceil((pending.submitAtMillis - Date.now()) / 1_000.0).toInt().coerceAtLeast(0)

    private fun schedule(pending: PendingPrediction) {
        if (pending.userId != activeUserId) return
        val key = key(pending.userId, pending.pollId)
        timeoutIds.remove(key)?.let { window.clearTimeout(it) }
        val delay = (pending.submitAtMillis - Date.now()).coerceAtLeast(0.0)
        timeoutIds[key] = window.setTimeout({
            timeoutIds.remove(key)
            submit(pending.userId, pending.pollId)
        }, delay.toInt())
    }

    private fun submit(userId: Long, pollId: Long) {
        val key = key(userId, pollId)
        val pending = queue.get(userId, pollId) ?: return
        if (pending.userId != activeUserId || !submittingKeys.add(key)) return
        scope.launch {
            runCatching { submitter(pending) }
                .onSuccess {
                    queue.remove(pending.userId, pending.pollId)
                    submittingKeys.remove(key)
                    persist()
                    onSubmitted(pending)
                }
                .onFailure { error ->
                    queue.remove(pending.userId, pending.pollId)
                    submittingKeys.remove(key)
                    persist()
                    onFailure(pending, error)
                }
        }
    }

    private fun loadOnce() {
        if (loaded) return
        loaded = true
        val stored = localStorage.getItem(STORAGE_KEY) ?: return
        runCatching { json.decodeFromString<List<PendingPrediction>>(stored) }
            .onSuccess { list ->
                queue.restore(list)
            }
            .onFailure {
                localStorage.removeItem(STORAGE_KEY)
            }
    }

    private fun persist() {
        val entries = queue.values()
        if (entries.isEmpty()) {
            localStorage.removeItem(STORAGE_KEY)
        } else {
            localStorage.setItem(STORAGE_KEY, json.encodeToString(entries))
        }
    }

    private fun key(userId: Long, pollId: Long): String = "$userId:$pollId"
}
